import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Variants } from '@common/types';
import { getDateWithoutTZ, getSelectedDateRangeString } from '@common/utils';
import { DateRangeDropdown, DateRangeSelection, SearchBar } from '@builder';
import { DropdownItem, SelectA11y } from '@base-app';
import { COMMON_TRANSLATION, LAYOUT_THREE } from '@constants/index';
import { STUDY_INSIGHTS_TRANSLATION } from '@modules/study-insights/constants';
import { SampleTrackingStatus } from '@modules/study-insights/types';
import { StyledDropdownFilters, StyledSampleTrackingFilter } from './SampleTrackingFilter.styles';
import { SampleTrackingFilterProps } from './SampleTrackingFilter.types';
import { useGoalsStore } from '@modules/study-insights/store';

const SampleTrackingFilter: FC<SampleTrackingFilterProps> = ({ onFilterChange }) => {
  const { t } = useTranslation([COMMON_TRANSLATION, STUDY_INSIGHTS_TRANSLATION]);
  const [queryFilter, setQueryFilter] = useState<string>('');
  const [dateRangeFilter, setDateRangeFilter] = useState<DateRangeSelection | undefined>();
  const [statusesFilter, setStatusesFilter] = useState<SampleTrackingStatus[]>([]);
  const startDateGoal = useGoalsStore((store) => store.lifetimeGoal?.startDate);
  const endDateGoal = new Date().toISOString();

  const getStatusKey = (status: SampleTrackingStatus): string => {
    if (status === SampleTrackingStatus.AWAITING) return 'awaiting_samples';
    if (status === SampleTrackingStatus.RECEIVED) return 'received_samples';
    if (status === SampleTrackingStatus.RECOLLECT) return 'recollects';
    return status.toLocaleLowerCase();
  };

  const options: DropdownItem[] = [...Object.values(SampleTrackingStatus)].map((value) => ({
    id: value,
    label: t(`sample_tracking.status.${getStatusKey(value)}`, { ns: STUDY_INSIGHTS_TRANSLATION }),
  }));

  const handleQueryFilterChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setQueryFilter(e?.target?.value);
  };

  const handleDateRangeSelect = (value: DateRangeSelection): void => {
    setDateRangeFilter(value);
  };

  const handleStatusesFilter = (values: DropdownItem[]): void => {
    setStatusesFilter([...values.map((item: DropdownItem) => item.id as SampleTrackingStatus)]);
  };

  const handleQueryFilterClear = (): void => {
    setQueryFilter('');
  };

  useEffect(() => {
    onFilterChange({
      query: queryFilter,
      dateRange: dateRangeFilter,
      statuses: statusesFilter,
    });
  }, [queryFilter, dateRangeFilter, statusesFilter]);

  return (
    <StyledSampleTrackingFilter>
      <SearchBar
        id="sample-tracking-search"
        label={''}
        name="sample-tracking-search"
        width="400px"
        onChange={handleQueryFilterChange}
        onClear={handleQueryFilterClear}
        isSensitive={true}
      />
      <StyledDropdownFilters>
        <DateRangeDropdown
          css={{ marginRight: '$4' }}
          dateRangeErrorTitle={t('selected_date_range.error.title', { ns: STUDY_INSIGHTS_TRANSLATION })}
          dateRangeErrorDescription={t('selected_date_range.error.description', {
            dateRange:
              startDateGoal &&
              endDateGoal &&
              getSelectedDateRangeString(
                new Date(getDateWithoutTZ(startDateGoal)),
                new Date(getDateWithoutTZ(endDateGoal)),
              ),
            ns: STUDY_INSIGHTS_TRANSLATION,
          })}
          minDate={startDateGoal ? new Date(getDateWithoutTZ(startDateGoal)) : undefined}
          maxDate={endDateGoal ? new Date(getDateWithoutTZ(endDateGoal)) : undefined}
          onSelect={handleDateRangeSelect}
          layout={LAYOUT_THREE}
        />
        <SelectA11y
          defaultValue={''}
          id="sample-tracking-status-filter"
          items={options}
          listWidth="250px"
          multipleSelector={true}
          onSelectedOption={handleStatusesFilter}
          placeholder={t('sample_status_filter')}
          testId="sample-tracking-status-filter"
          value={''}
          variant={Variants.SECONDARY}
        />
      </StyledDropdownFilters>
    </StyledSampleTrackingFilter>
  );
};

export default SampleTrackingFilter;
